/**
 * Shared onboarding-wizard types. Split out of `app/business-onboarding.tsx`
 * so step components can import `OnboardingStep`/`TeamSize` for their own
 * props without importing the route file that in turn imports them. Type-only
 * circular imports happen to compile, but this shared file is the correct
 * shape.
 */
export type OnboardingStep =
  /** The brand front door - mark, name, one sentence, one button. Sits BEFORE
   *  `welcome` and asks for nothing (`steps/IntroStep.tsx`, 2026-08-16). */
  | 'intro'
  | 'welcome'
  /** Name + address + phone. `location` used to be a separate step; it was
   *  merged in here so the wizard opens with everything a customer needs to
   *  find the place, in one screen. */
  | 'basics'
  /** MAIN trade + any additional ones (a hair salon that also does nails). */
  | 'category'
  | 'services'
  | 'team-size'
  | 'staff'
  | 'staff-hours'
  | 'description'
  | 'photo'
  /** Offer to bring the owner's address book across as clients. Opt-in only;
   *  the import itself runs AFTER submit, once a business exists to own the
   *  clients - see `business-onboarding.tsx`'s own doc comment. */
  | 'contacts'
  | 'review';

export type TeamSize = 'solo' | 'small' | 'large';

/** How many staff rows a team-size pick allows this wizard to add on the
 *  'staff' step - `null` means uncapped. "2-3" is capped at its upper bound
 *  (3); solo is capped at 1 (enforced structurally by the solo variant
 *  itself, which has no add-another affordance at all); "4+" is genuinely
 *  open-ended, so it has no cap. */
export const MAX_STAFF: Record<TeamSize, number | null> = {
  solo: 1,
  small: 3,
  large: null,
};
